import React, { useState } from "react";
import styled, { css } from "styled-components";
import { SCREEN_SM } from "sketon/ui/theme";
import { DEFAULT_BOOK_COVER } from "sketon/ui/theme/DefaultThemeSetting";

/* Small enough that the build inlines it as a data URI, so it paints with
   no network request of its own. */
const FALLBACK_COVER = require("assets/images/" + DEFAULT_BOOK_COVER);

/*
 * Covers come from many sources and their aspect ratios run from about
 * 0.62 to 1.0 (w/h). Height is therefore the fixed dimension and width
 * follows each cover's own ratio, clamped to the column width. A cover
 * wider than that clamp overflows the box equally on both sides and is
 * cropped by `overflow: hidden`, so what survives is the middle of the
 * image rather than its left edge.
 */
const StyledBookCover = styled.div`
  height: 130px;
  /* Prefixed first for older WebKit (in-app browsers), standard value wins
     wherever it is understood. */
  width: -webkit-fit-content;
  width: fit-content;
  max-width: 100px;
  flex-shrink: 0;

  margin-top: 0;
  margin-left: 0;
  padding: 0 0 0 0;

  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;

  background: #EFE6D2;

  @media (max-width: ${SCREEN_SM}) {
    height: 2.35rem;
    max-width: 1.7rem;
  }
`;

/*
 * `$pending` takes the real cover out of flow while it is still loading, so
 * it cannot size the box: an <img> with no bytes yet reports zero intrinsic
 * width, which collapsed the whole box to 0px wide. Out of flow it still
 * counts as in-viewport for lazy loading, it just stays invisible until it
 * has something to show.
 */
const StyledCoverImg = styled.img<{ $pending?: boolean }>`
  display: block;
  height: 100%;
  width: auto;
  max-width: none;
  flex-shrink: 0;
  object-fit: cover;
  object-position: center;

  ${(props) =>
    props.$pending &&
    css`
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0;
      pointer-events: none;
    `}
`;

const StyledBookCoverMask = styled.div`
  position: absolute;
  background: transparent;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  border: 2px solid #ffffff;
  z-index: 2;
`;

export interface BookCoverProps {
  src: string;
  /** Book title, used as the image's alt text. */
  alt?: string;
}

export const BookCover: React.FC<BookCoverProps> = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  const showReal = loaded && !failed;

  return (
    <StyledBookCover>
      {/* Holds the box open at the default cover's own ratio and is what the
          reader sees until the real cover arrives — or for good, if it never
          does. */}
      {!showReal && (
        <StyledCoverImg src={FALLBACK_COVER} alt="" aria-hidden="true" />
      )}

      <StyledCoverImg
        src={src}
        alt={showReal ? alt ?? "" : ""}
        aria-hidden={showReal ? undefined : true}
        loading="lazy"
        decoding="async"
        $pending={!showReal}
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
      />

      <StyledBookCoverMask />
    </StyledBookCover>
  );
};

export default BookCover;
